import React, { useState, useEffect, useMemo } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Shield, RefreshCw, Filter } from 'lucide-react';

/** Read-only Audit Log dashboard. Admin only.
 *  Surfaces the security-category entries from /api/audit so admins can
 *  spot suspicious activity (failed logins, lockouts, role changes, admin
 *  resets) at a glance.
 */
const AuditLog = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    action: '',
    entity_type: 'security',
    date_from: '',
    date_to: '',
  });

  const fetchEntries = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await axiosInstance.get('/audit', { params });
      setEntries(data.entries || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load audit log');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEntries(); /* eslint-disable-next-line */ }, []);

  const stats = useMemo(() => {
    const counts = { login_failed: 0, login_success: 0, account_locked: 0, admin_reset: 0, password_change: 0 };
    entries.forEach(e => { if (counts[e.action] !== undefined) counts[e.action] += 1; });
    return counts;
  }, [entries]);

  const actionStyle = (a) => {
    if (a === 'login_failed' || a === 'account_locked') return 'bg-red-100 text-red-800';
    if (a === 'login_success') return 'bg-green-100 text-green-800';
    if (a === 'admin_reset' || a === 'password_change') return 'bg-amber-100 text-amber-800';
    if (a === 'user_disabled' || a === 'user_deleted') return 'bg-rose-100 text-rose-800';
    if (a === 'role_changed' || a === 'permissions_changed') return 'bg-indigo-100 text-indigo-800';
    return 'bg-slate-100 text-slate-800';
  };

  return (
    <div data-testid="audit-log-page">
      <div className="page-header">
        <h2 className="page-title flex items-center gap-2"><Shield className="w-6 h-6 text-indigo-600" /> Security Audit Log</h2>
        <button data-testid="refresh-audit" onClick={fetchEntries} className="btn-primary inline-flex items-center gap-2">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        {[
          ['Successful Logins', stats.login_success, 'text-green-700'],
          ['Failed Logins', stats.login_failed, 'text-red-700'],
          ['Account Locks', stats.account_locked, 'text-red-700'],
          ['Admin Resets', stats.admin_reset, 'text-amber-700'],
          ['Password Changes', stats.password_change, 'text-blue-700'],
        ].map(([label, val, color]) => (
          <div key={label} className="bg-white rounded-lg border p-3">
            <p className="text-xs text-slate-500">{label}</p>
            <p className={`text-2xl font-bold ${color}`}>{val}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border p-3 mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="text-xs text-slate-500">Action</label>
          <select
            data-testid="audit-filter-action"
            value={filters.action}
            onChange={(e) => setFilters({ ...filters, action: e.target.value })}
            className="form-select"
          >
            <option value="">All actions</option>
            <option value="login_success">login_success</option>
            <option value="login_failed">login_failed</option>
            <option value="account_locked">account_locked</option>
            <option value="password_change">password_change</option>
            <option value="admin_reset">admin_reset</option>
            <option value="user_created">user_created</option>
            <option value="user_disabled">user_disabled</option>
            <option value="user_activated">user_activated</option>
            <option value="user_deleted">user_deleted</option>
            <option value="user_unlocked">user_unlocked</option>
            <option value="role_changed">role_changed</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-slate-500">From</label>
          <input
            type="date" className="form-input"
            value={filters.date_from}
            onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
          />
        </div>
        <div>
          <label className="text-xs text-slate-500">To</label>
          <input
            type="date" className="form-input"
            value={filters.date_to}
            onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
          />
        </div>
        <button data-testid="apply-audit-filter" onClick={fetchEntries} className="btn-primary inline-flex items-center gap-2">
          <Filter className="w-4 h-4" /> Apply
        </button>
      </div>

      {/* Table */}
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Timestamp</th>
              <th>Action</th>
              <th>Actor</th>
              <th>Target</th>
              <th>IP</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="6" className="text-center py-8 text-slate-500">Loading...</td></tr>
            ) : entries.length === 0 ? (
              <tr><td colSpan="6" className="text-center py-8 text-slate-500">No audit entries</td></tr>
            ) : entries.map((e) => (
              <tr key={e.id}>
                <td className="text-xs whitespace-nowrap">{new Date(e.ts).toLocaleString()}</td>
                <td>
                  <span className={`px-2 py-1 rounded text-xs font-semibold ${actionStyle(e.action)}`}>
                    {e.action}
                  </span>
                </td>
                <td className="text-sm">{e.user_name || e.user_id || '-'}</td>
                <td className="text-sm">{e.metadata?.target_email || e.entity_id || '-'}</td>
                <td className="text-xs text-slate-600">{e.metadata?.ip || '-'}</td>
                <td className="text-xs text-slate-600">
                  {e.metadata?.reason && <span className="mr-2">reason: {e.metadata.reason}</span>}
                  {e.metadata?.attempts && <span className="mr-2">attempts: {e.metadata.attempts}</span>}
                  {e.metadata?.from && <span className="mr-2">from: {e.metadata.from} → {e.metadata.to}</span>}
                  {e.metadata?.user_agent && (
                    <span className="text-slate-400 truncate inline-block max-w-xs" title={e.metadata.user_agent}>
                      UA: {e.metadata.user_agent.slice(0, 40)}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AuditLog;
